import { Mutex } from "async-mutex";
import { RoutesE } from "../../../routes/routesData";
import fetchTokens from "./fetchTokens";
import { FetchTokensGrantTypeE } from "./models";
import {
  getCurrentUserProfile,
  getLoggedUserToken,
  handleCurrentUserSessionExpired,
  notifyAuthStorageSync,
  updateTokens,
} from "../tokenStorage";

export const authRefreshMutex = new Mutex();

let authRefreshPaused = false;

export const setAuthRefreshPaused = (paused: boolean) => {
  authRefreshPaused = paused;
};

export const isAuthRefreshPaused = () => authRefreshPaused;

/**
 * Refreshes the current user's access token. Uses a shared mutex so parallel
 * 401 handlers (RTK Query + authenticatedFetch) only hit /token once.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  if (authRefreshPaused) {
    return getLoggedUserToken() ?? null;
  }

  if (authRefreshMutex.isLocked()) {
    await authRefreshMutex.waitForUnlock();
    return getLoggedUserToken() ?? null;
  }

  const release = await authRefreshMutex.acquire();

  try {
    const user = getCurrentUserProfile();
    const refreshToken = user?.refreshToken;

    if (!refreshToken) {
      return null;
    }

    const [accessToken, newRefreshToken] = await fetchTokens(
      FetchTokensGrantTypeE.REFRESH_TOKEN,
      { refresh_token: refreshToken }
    );

    if (!accessToken) {
      return null;
    }

    updateTokens([accessToken, newRefreshToken ?? refreshToken]);
    notifyAuthStorageSync();

    return accessToken;
  } finally {
    release();
  }
};

export const handleRefreshFailure = (): void => {
  const shouldRedirectToLogin = handleCurrentUserSessionExpired();
  notifyAuthStorageSync();

  if (shouldRedirectToLogin) {
    window.location.pathname = RoutesE.LOGIN;
  } else {
    window.location.reload();
  }
};
